import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';
import { fpColors } from '../../../styles/theme';
import { PrimaryCTA } from '../../../components/PrimaryCTA';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { selectMemberNameById } from '../../sharing/groupsSlice';
import {
  recallItem,
  selectCustodyLoading,
  selectCustodyError,
  clearCustodyError,
} from '../custodySlice';

/**
 * RecallSheet — Sprint 4 / Chunk 10B · FE-10.3 (Flow I).
 *
 * Owner-initiated urgent recall. Triggered when the owner needs an
 * item back NOW (different from a normal reminder).
 *
 * Posts to `POST /inventory/{id}/recall` (BE-10.6) which writes a
 * high-priority `borrow_requests` row and sends a high-priority
 * email to the current holder. Custody does NOT flip immediately —
 * the holder must acknowledge via the holder-side recall UI (their
 * receipt of a `RECALL_ACK` event flips custody). Today the holder
 * sees the recall in their email + crew Activity feed.
 *
 * Design: intentionally distinct visual language from HandOffSheet —
 * `error` accent, urgent tone copy, prominent reason picker. The
 * backend body is just `{ reason }` so the picker compiles to a
 * single string before submit.
 */

interface RecallSheetProps {
  visible: boolean;
  itemId: number;
  itemName: string;
  /** Current holder — used in the modal copy ("Recall from Dylan"). */
  custodianUserId?: number | null;
  /** Optional callback fired after a successful submit. */
  onSuccess?: () => void;
  onClose: () => void;
}

interface ReasonOption {
  key: string;
  label: string;
  /** Plain-text reason that goes to the server (and into the email). */
  reasonText: string;
}

const REASONS: ReasonOption[] = [
  {
    key: 'active-job',
    label: 'Need it for an active job',
    reasonText: 'Need it for an active job',
  },
  {
    key: 'end-of-loan',
    label: 'End of loan',
    reasonText: 'Loan period has ended',
  },
  {
    key: 'other',
    label: 'Other',
    reasonText: '', // user fills in via the free-text field
  },
];

export const RecallSheet: React.FC<RecallSheetProps> = ({
  visible,
  itemId,
  itemName,
  custodianUserId,
  onSuccess,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectCustodyLoading(itemId));
  const error = useAppSelector(selectCustodyError(itemId));
  const holderName = useAppSelector(selectMemberNameById(custodianUserId ?? null));

  const [reasonKey, setReasonKey] = useState<string>('active-job');
  const [otherText, setOtherText] = useState<string>('');

  useEffect(() => {
    if (visible) {
      setReasonKey('active-job');
      setOtherText('');
      dispatch(clearCustodyError(itemId));
    }
  }, [visible, itemId, dispatch]);

  const compiledReason = useMemo(() => {
    if (reasonKey === 'other') {
      return otherText.trim() || null;
    }
    const opt = REASONS.find((r) => r.key === reasonKey);
    return opt?.reasonText ?? null;
  }, [reasonKey, otherText]);

  const canSubmit =
    !loading.recall &&
    (reasonKey !== 'other' || otherText.trim().length > 0);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const result = await dispatch(
      recallItem({
        itemId,
        request: { reason: compiledReason },
      }),
    );

    if (recallItem.fulfilled.match(result)) {
      Toast.show({
        type: 'success',
        text1: 'Recall sent',
        text2: holderName
          ? `Notified ${holderName} — ${itemName}`
          : `Notified the holder — ${itemName}`,
      });
      onSuccess?.();
      onClose();
    } else {
      Toast.show({
        type: 'error',
        text1: 'Recall failed',
        text2: (result.payload as string) ?? 'Please try again.',
      });
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View
          style={styles.sheet}
          accessible
          accessibilityViewIsModal
          accessibilityLabel="Recall item"
          // @ts-ignore — RN Web only
          role="dialog"
          aria-modal={true}
          testID="recall-sheet"
        >
          {/* Header — uses error tone instead of neutral so the urgent
              context registers immediately. */}
          <View style={styles.header}>
            <View style={styles.headerIconWrap}>
              <MaterialIcons
                name="priority-high"
                size={20}
                color={fpColors.error}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.title}>Recall item</Text>
              {/* Gap #107 (2026-05-27 audit) — the subtitle used to read
                  "{itemName} · with {holderName}" on a single line with
                  ellipsis, which cut the holder's name on a 390px
                  viewport ("…with Bl…"). Drop the holder clause here —
                  the holder is named in the warning banner below so
                  the user still sees who's being recalled, just without
                  truncating the item name. */}
              <Text style={styles.subtitle} numberOfLines={1}>
                {itemName}
              </Text>
            </View>
            <Pressable
              onPress={onClose}
              hitSlop={8}
              accessibilityLabel="Close"
              accessibilityRole="button"
              style={styles.closeBtn}
            >
              <MaterialIcons name="close" size={22} color={fpColors.onSurface} />
            </Pressable>
          </View>

          <ScrollView style={styles.body} keyboardShouldPersistTaps="handled">
            <View style={styles.banner}>
              {/* Gap #107 follow-on — holder name moved from the
                  subtitle into the warning body where it can wrap
                  freely without truncation. */}
              <Text style={styles.bannerText}>
                A high-priority email goes to{' '}
                {holderName ? `${holderName}.` : 'the holder.'} They'll
                be asked to return ASAP.
              </Text>
            </View>

            <Text style={styles.sectionLabel}>Reason</Text>
            {REASONS.map((opt) => {
              const selected = opt.key === reasonKey;
              return (
                <Pressable
                  key={opt.key}
                  onPress={() => setReasonKey(opt.key)}
                  accessibilityRole="radio"
                  accessibilityState={{ selected }}
                  accessibilityLabel={opt.label}
                  style={[
                    styles.reasonRow,
                    selected && styles.reasonRowSelected,
                  ]}
                  testID={`recall-reason-${opt.key}`}
                >
                  <MaterialIcons
                    name={
                      selected ? 'radio-button-checked' : 'radio-button-unchecked'
                    }
                    size={20}
                    color={selected ? fpColors.primary : fpColors.outline}
                  />
                  <Text
                    style={[
                      styles.reasonLabel,
                      selected && styles.reasonLabelSelected,
                    ]}
                  >
                    {opt.label}
                  </Text>
                </Pressable>
              );
            })}

            {reasonKey === 'other' ? (
              <View style={{ marginTop: 12 }}>
                <Text style={styles.sectionLabel}>Tell the holder why</Text>
                <TextInput
                  value={otherText}
                  onChangeText={setOtherText}
                  multiline
                  numberOfLines={3}
                  placeholder="e.g. tenant moving in this weekend"
                  placeholderTextColor={fpColors.outline}
                  style={[styles.textInput, styles.textArea]}
                  testID="recall-other-text"
                />
              </View>
            ) : null}

            {error ? (
              <View style={styles.errorBanner}>
                <MaterialIcons name="error-outline" size={18} color={fpColors.error} />
                <Text style={styles.errorText} numberOfLines={2}>
                  {error}
                </Text>
              </View>
            ) : null}
          </ScrollView>

          <View style={styles.footer} testID="recall-submit-wrap">
            <PrimaryCTA
              label={loading.recall ? 'Sending recall…' : 'Send recall'}
              onPress={handleSubmit}
              disabled={!canSubmit}
              loading={loading.recall}
              iconRight={null}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

/* ──────────────────────────────────────────────────────────────────────
 * Styles
 * ────────────────────────────────────────────────────────────────────── */

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(28, 23, 33, 0.55)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: fpColors.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '90%',
    paddingBottom: Platform.OS === 'ios' ? 24 : 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
  },
  headerIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: fpColors.errorContainer,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.onSurface,
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.outline,
    marginTop: 2,
  },
  closeBtn: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 16,
  },
  body: {
    paddingHorizontal: 20,
    paddingTop: 18,
  },
  banner: {
    backgroundColor: fpColors.errorContainer,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    marginBottom: 18,
  },
  bannerText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.error,
    lineHeight: 18,
  },
  sectionLabel: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    marginBottom: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  reasonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: fpColors.surfaceContainerLow,
    marginBottom: 6,
  },
  reasonRowSelected: {
    backgroundColor: fpColors.primaryContainer,
  },
  reasonLabel: {
    flex: 1,
    fontFamily: 'Inter_600SemiBold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  reasonLabelSelected: {
    color: fpColors.onSurface,
  },
  textInput: {
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontFamily: 'Inter_400Regular',
    fontSize: 15,
    color: fpColors.onSurface,
    backgroundColor: fpColors.surfaceContainerLowest,
  },
  textArea: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: fpColors.errorContainer,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    marginTop: 16,
  },
  errorText: {
    flex: 1,
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.error,
  },
  footer: {
    paddingHorizontal: 20,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: fpColors.outlineVariant,
  },
});

export default RecallSheet;
